import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Buton, Etiket, Girdi, Iskelet, toast } from '@emanetx/ui';
import type { Kullanici, KullaniciDurumu } from '@emanetx/sdk';
import { useKullaniciGuncelle, useKullaniciOlustur, useRoller } from './api.js';

interface Ozellikler {
  acik: boolean;
  onDegisim: (a: boolean) => void;
  kullanici?: Kullanici | null;
}

export function KullaniciDialog({ acik, onDegisim, kullanici }: Ozellikler) {
  const duzenleme = !!kullanici;
  const olustur = useKullaniciOlustur();
  const guncelle = useKullaniciGuncelle();
  const roller = useRoller();

  const [eposta, setEposta] = useState('');
  const [parola, setParola] = useState('');
  const [adSoyad, setAdSoyad] = useState('');
  const [telefon, setTelefon] = useState('');
  const [durum, setDurum] = useState<KullaniciDurumu>('AKTIF');
  const [secilenRoller, setSecilenRoller] = useState<string[]>([]);

  useEffect(() => {
    if (!acik) return;
    if (kullanici) {
      setEposta(kullanici.eposta);
      setParola('');
      setAdSoyad(kullanici.adSoyad);
      setTelefon(kullanici.telefon ?? '');
      setDurum(kullanici.durum);
      setSecilenRoller(kullanici.roller);
    } else {
      setEposta('');
      setParola('');
      setAdSoyad('');
      setTelefon('');
      setDurum('AKTIF');
      setSecilenRoller([]);
    }
  }, [acik, kullanici]);

  const calisiyor = olustur.isPending || guncelle.isPending;
  const formGecerli = duzenleme
    ? adSoyad.trim().length >= 2
    : /^.+@.+\..+$/.test(eposta) && parola.length >= 8 && adSoyad.trim().length >= 2;

  const toggleRol = (ad: string) => {
    setSecilenRoller((onceki) =>
      onceki.includes(ad) ? onceki.filter((r) => r !== ad) : [...onceki, ad],
    );
  };

  const gonder = async () => {
    try {
      if (duzenleme && kullanici) {
        await guncelle.mutateAsync({
          id: kullanici.id,
          adSoyad: adSoyad.trim(),
          telefon: telefon || null,
          durum,
          rolAdlari: secilenRoller,
        });
        toast.success('Kullanıcı güncellendi');
      } else {
        await olustur.mutateAsync({
          eposta: eposta.toLowerCase().trim(),
          parola,
          adSoyad: adSoyad.trim(),
          telefon: telefon || undefined,
          rolAdlari: secilenRoller,
          dilTercihi: 'tr',
        });
        toast.success('Kullanıcı oluşturuldu');
      }
      onDegisim(false);
    } catch (err) {
      const m =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'İşlem başarısız';
      toast.error(m);
    }
  };

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[92vw] max-w-xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold">
              {duzenleme ? 'Kullanıcıyı Düzenle' : 'Yeni Kullanıcı'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Kapat" className="text-fume-400 hover:text-fume-700">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (formGecerli && !calisiyor) void gonder();
            }}
            className="space-y-4"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Etiket htmlFor="ep">
                  E-posta <span className="text-red-500">*</span>
                </Etiket>
                <Girdi
                  id="ep"
                  type="email"
                  value={eposta}
                  onChange={(e) => setEposta(e.target.value)}
                  disabled={duzenleme}
                  required={!duzenleme}
                />
              </div>
              {!duzenleme && (
                <div>
                  <Etiket htmlFor="pa">
                    Parola <span className="text-red-500">*</span>
                  </Etiket>
                  <Girdi
                    id="pa"
                    type="password"
                    value={parola}
                    onChange={(e) => setParola(e.target.value)}
                    minLength={8}
                    required
                    placeholder="min 8 karakter"
                  />
                </div>
              )}
              <div>
                <Etiket htmlFor="ad">
                  Ad Soyad <span className="text-red-500">*</span>
                </Etiket>
                <Girdi
                  id="ad"
                  value={adSoyad}
                  onChange={(e) => setAdSoyad(e.target.value)}
                  required
                />
              </div>
              <div>
                <Etiket htmlFor="tel">Telefon</Etiket>
                <Girdi
                  id="tel"
                  value={telefon}
                  onChange={(e) => setTelefon(e.target.value)}
                  placeholder="+90 5XX..."
                />
              </div>
              {duzenleme && (
                <div>
                  <Etiket htmlFor="du">Durum</Etiket>
                  <select
                    id="du"
                    value={durum}
                    onChange={(e) => setDurum(e.target.value as KullaniciDurumu)}
                    className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 mt-1 h-11 w-full rounded-lg border bg-white px-3 text-sm"
                  >
                    <option value="AKTIF">Aktif</option>
                    <option value="PASIF">Pasif</option>
                    <option value="ENGELLI">Engelli</option>
                  </select>
                </div>
              )}
            </div>

            <div>
              <Etiket>Roller</Etiket>
              {roller.isLoading ? (
                <Iskelet className="mt-1 h-20" />
              ) : (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(roller.data ?? []).map((r) => {
                    const secili = secilenRoller.includes(r.ad);
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => toggleRol(r.ad)}
                        title={r.aciklama ?? ''}
                        className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                          secili
                            ? 'border-marka-500 bg-marka-500 text-fume-900'
                            : 'border-fume-200 text-fume-600 hover:bg-fume-50 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-300 bg-white'
                        }`}
                      >
                        {r.ad}
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="text-fume-400 mt-1 text-xs">
                Rol etiketinin üzerine gel: tam izin listesi tooltip'te.
              </p>
            </div>

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Buton stil="cizgili" boyut="orta" disabled={calisiyor}>
                  İptal
                </Buton>
              </Dialog.Close>
              <Buton type="submit" disabled={!formGecerli || calisiyor}>
                {calisiyor ? 'Kaydediliyor...' : duzenleme ? 'Güncelle' : 'Oluştur'}
              </Buton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
